import { useTranslation } from '~/app/i18n/client'

export const MAX_EARN_RATE_FILTER_OPTION = 6

interface EarnContext {
  /*
   * Indicates if the affiliate has variable earning rates,
   * depending on the category to which the purchased product is related.
   */
  hasVariableCategoryEarnRate?: boolean
  /*
   * Represents the base points earned by a member
   */
  flatEarnRate?: number
  /*
   * Represents the rate of points earned by a member per dollar spent
   */
  earnMultiplier?: number
}

interface EarnStatement {
  /*
   * Full text description of the earn statement
   */
  summary: string
  /*
   * Full text description of the earn statement for screen readers
   */
  screenReaderSummary: string
}

export const useEarnStatement = () => {
  const { t: translate } = useTranslation()

  const formatPoints = (points: number, isScreenReader = false) =>
    points === 1
      ? isScreenReader
        ? translate('retailers.point')
        : 'pt'
      : isScreenReader
      ? translate('retailers.points')
      : 'pts'

  const formatPointsPerDollar = (points: number, isScreenReader = false) => {
    const perDollarSuffix = isScreenReader
      ? ` ${translate('retailers.perDollar')}`
      : '/$'
    return `${formatPoints(points, isScreenReader)}${perDollarSuffix}`
  }

  const getEarnText = (
    flatRate: number,
    multiplier: number,
    isScreenReader = false,
  ) => {
    const flatText =
      flatRate > 0
        ? `${flatRate} ${formatPoints(flatRate, isScreenReader)}`
        : ''
    const multiplierText =
      multiplier > 0
        ? `${multiplier} ${formatPointsPerDollar(multiplier, isScreenReader)}`
        : ''

    return flatText && multiplierText
      ? `${flatText} + ${multiplierText}`
      : flatText || multiplierText
  }

  const getEarnStatement = ({
    flatEarnRate = 0,
    earnMultiplier = 0,
    hasVariableCategoryEarnRate = false,
  }: EarnContext): EarnStatement => {
    let finalFlatEarnRate = flatEarnRate
    let finalEarnMultiplier = earnMultiplier

    if (
      flatEarnRate > 0 &&
      !hasVariableCategoryEarnRate &&
      earnMultiplier > 0
    ) {
      finalEarnMultiplier = 0
    }

    if (
      (earnMultiplier > 0 && hasVariableCategoryEarnRate) ||
      (flatEarnRate > 0 && earnMultiplier === 0 && hasVariableCategoryEarnRate)
    ) {
      finalFlatEarnRate = 0
    }

    // If neither earn rate applies, return empty summaries
    if (finalFlatEarnRate === 0 && finalEarnMultiplier === 0) {
      return { summary: '', screenReaderSummary: '' }
    }

    const summary = getEarnText(finalFlatEarnRate, finalEarnMultiplier)
    const screenReaderSummary = getEarnText(
      finalFlatEarnRate,
      finalEarnMultiplier,
      true,
    )

    const prefix = hasVariableCategoryEarnRate
      ? translate('retailers.earnUpTo')
      : translate('retailers.earn')

    return {
      summary: `${prefix} ${summary}`.trim(),
      screenReaderSummary: `${prefix} ${screenReaderSummary}`.trim(),
    }
  }

  const mapEarnRateOptions = (options: any[]) => {
    const sortedOptions = [...options].sort(
      (a, b) => parseInt(b.name) - parseInt(a.name),
    )

    return sortedOptions
      .slice(-MAX_EARN_RATE_FILTER_OPTION)
      .map((option, index) => ({
        id: option.name,
        key: option.name,
        comparisonOperator: index === 0 ? '>=' : '==',
        text: `${translate('retailers.earn')} ${option.name} ${
          index === 0
            ? options.length > MAX_EARN_RATE_FILTER_OPTION
              ? 'pts/$ ' + translate('filters.orMore')
              : 'pts/$'
            : option.key === '1'
            ? 'pt/$'
            : 'pts/$'
        }`,
      }))
  }

  return { getEarnStatement, mapEarnRateOptions }
}
